import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Printer, Download } from "lucide-react";
import { format, startOfMonth, endOfMonth, parseISO } from "date-fns";
import { formatTime, tzForCountry, tzShort } from "@/lib/timezone";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { BRAND } from "@/lib/brand";

/**
 * Publish-ready employee report. One employee, one date range, one page
 * covering: presence infographics, working-hours graph, breach frequency
 * chart, expected vs actual hours, and a day-by-day table. Designed to
 * print cleanly (Ctrl/Cmd+P → Save as PDF).
 */
const COLORS = {
  present: "#34d399",
  absent: "#ef4444",
  late: "#fbbf24",
  incomplete: "#fb923c",
  breach: "#f472b6",
  future: "#475569",
  ok: "#60a5fa",
  brand: "#1e3a8a",
};

export default function EmployeeReport() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [countries, setCountries] = useState([]);
  const [centers, setCenters] = useState([]);
  const [policy, setPolicy] = useState(null);
  const today = new Date();
  const [range, setRange] = useState({
    start: searchParams.get("start") || format(startOfMonth(today), "yyyy-MM-dd"),
    end: searchParams.get("end") || format(endOfMonth(today), "yyyy-MM-dd"),
  });
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, c, ce, p] = await Promise.all([
          api.get("/users"),
          api.get("/countries"),
          api.get("/centers"),
          api.get("/policy"),
        ]);
        setUser(u.data.find((x) => x.id === id) || false);
        setCountries(c.data);
        setCenters(ce.data);
        setPolicy(p.data);
      } catch { setUser(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    (async () => {
      try {
        const params = { start: range.start, end: range.end };
        if (user.country_id) params.country_id = user.country_id;
        const { data } = await api.get("/attendance/matrix", { params });
        setMatrix(data);
      } finally { setLoading(false); }
    })();
    setSearchParams({ start: range.start, end: range.end }, { replace: true });
  }, [user, range.start, range.end, setSearchParams]);

  const activeTz = useMemo(() => tzForCountry(countries, user?.country_id), [countries, user]);
  const tzLabel = useMemo(() => tzShort(activeTz), [activeTz]);

  const empRow = useMemo(() => matrix?.employees?.find((e) => e.id === id) || null, [matrix, id]);

  // Aggregate everything the report needs, in one pass.
  const stats = useMemo(() => {
    if (!empRow || !matrix || !policy) return null;
    const todayIso = new Date().toISOString().slice(0, 10);
    const minHours = Number(policy.min_working_hours || matrix.min_hours || 8);

    let totalHours = 0;
    let workingDaysInRange = 0;
    let totalBreachEvents = 0;
    let daysWithBreaches = 0;
    let workedDays = 0;
    let longest = null;
    let shortest = null;
    const dailyBars = [];   // for hours line chart
    const breachBars = [];  // for breaches bar chart

    for (const d of matrix.days) {
      const cell = empRow.days[d];
      const day = parseISO(d);
      const dow = day.getDay();
      const isWeekday = dow !== 0 && dow !== 6;
      const elapsed = d <= todayIso;
      if (isWeekday && elapsed) workingDaysInRange += 1;

      const hours = Number(cell?.hours || 0);
      const br = Number(cell?.breaches || 0);
      totalHours += hours;
      totalBreachEvents += br;
      if (cell?.status === "Present") {
        if (br > 0) daysWithBreaches += 1;
        if (hours > 0) {
          workedDays += 1;
          if (!longest || hours > longest.hours) longest = { date: d, hours };
          if (!shortest || hours < shortest.hours) shortest = { date: d, hours };
        }
      }
      dailyBars.push({ date: d.slice(5), hours: Number(hours.toFixed(2)) });
      breachBars.push({ date: d.slice(5), breaches: br });
    }

    const weeksElapsed = workingDaysInRange / 5;
    const dailyAvg = workingDaysInRange ? totalHours / workingDaysInRange : 0;
    const weeklyAvg = weeksElapsed > 0 ? totalHours / weeksElapsed : 0;
    const expectedHours = workingDaysInRange * minHours;
    const compliance = expectedHours > 0 ? Math.min(1, totalHours / expectedHours) : 0;

    const presenceData = [
      { name: "Present", value: empRow.summary.present, color: COLORS.present },
      { name: "Late",    value: empRow.summary.late,    color: COLORS.late },
      { name: "Absent",  value: empRow.summary.absent,  color: COLORS.absent },
      { name: "Missing out", value: empRow.summary.missing_out, color: COLORS.incomplete },
    ].filter((x) => x.value > 0);

    return {
      totalHours, workingDaysInRange, minHours, expectedHours, compliance,
      workedDays, dailyAvg, weeklyAvg,
      totalBreachEvents, daysWithBreaches,
      longest, shortest,
      dailyBars, breachBars, presenceData,
      summary: empRow.summary,
    };
  }, [empRow, matrix, policy]);

  const countryName = useMemo(
    () => countries.find((c) => c.id === user?.country_id)?.name || "—",
    [countries, user]
  );
  const centerNames = useMemo(() => {
    if (!user?.center_ids) return "";
    return user.center_ids.map((cid) => centers.find((c) => c.id === cid)?.name).filter(Boolean).join(" · ");
  }, [user, centers]);

  if (user === false) {
    return <div className="p-8"><p className="text-red-300">Employee not found.</p><Link to="/admin/employees" className="text-primary underline text-sm">Back to employees</Link></div>;
  }
  if (!user) {
    return <div className="p-8 grid place-items-center"><Loader2 className="animate-spin h-6 w-6 text-muted-foreground" /></div>;
  }

  const jumpThisMonth = () => setRange({ start: format(startOfMonth(new Date()), "yyyy-MM-dd"), end: format(endOfMonth(new Date()), "yyyy-MM-dd") });
  const jumpLastMonth = () => {
    const prev = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
    setRange({ start: format(startOfMonth(prev), "yyyy-MM-dd"), end: format(endOfMonth(prev), "yyyy-MM-dd") });
  };

  return (
    <div className="print:bg-white print:text-black bg-background text-foreground">
      {/* ---- Non-print toolbar ---- */}
      <div className="print:hidden border-b border-border bg-background sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex flex-wrap items-end gap-3 p-4">
          <Link
            to={`/admin/employees/${id}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> Back to employee
          </Link>
          <div className="flex-1" />
          <div>
            <Label className="label-mono">From</Label>
            <Input type="date" value={range.start} max={range.end} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="rounded-none" />
          </div>
          <div>
            <Label className="label-mono">To</Label>
            <Input type="date" value={range.end} min={range.start} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="rounded-none" />
          </div>
          <Button variant="outline" onClick={jumpThisMonth} className="rounded-none border-border">This month</Button>
          <Button variant="outline" onClick={jumpLastMonth} className="rounded-none border-border">Last month</Button>
          <Button
            data-testid="print-report-btn"
            onClick={() => window.print()}
            className="rounded-none btn-primary-brand"
          >
            <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {loading && (
        <div className="py-16 grid place-items-center">
          <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
        </div>
      )}

      {!loading && stats && (
        <article className="max-w-5xl mx-auto p-6 space-y-6 print:p-0 print:space-y-4 print:max-w-full">
          {/* ---- Report header ---- */}
          <header className="border-b border-border pb-4 flex items-start justify-between print:border-b-2 print:border-black">
            <div>
              <div className="label-mono">Attendance Report</div>
              <h1 className="font-display font-bold text-3xl mt-1">{user.name}</h1>
              <div className="text-sm text-muted-foreground mt-1 font-mono-brand print:text-black">
                {user.employee_id} · {user.email} · {countryName} · {centerNames || "no centers"}
              </div>
              <div className="text-xs text-muted-foreground font-mono-brand mt-1 print:text-black">
                {format(parseISO(range.start), "d MMM yyyy")} → {format(parseISO(range.end), "d MMM yyyy")} · Times in {tzLabel}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display font-bold text-xl">{BRAND.fullName}</div>
              <div className="label-mono">Generated {format(new Date(), "d MMM yyyy · HH:mm")}</div>
            </div>
          </header>

          {/* ---- Hero KPI row ---- */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Actual hours" value={stats.totalHours.toFixed(1) + " h"} accent={COLORS.present} />
            <Kpi label="Expected hours" value={stats.expectedHours.toFixed(1) + " h"} sub={`${stats.minHours} h × ${stats.workingDaysInRange} working days`} />
            <Kpi label="Compliance" value={`${(stats.compliance * 100).toFixed(0)}%`} accent={stats.compliance >= 0.9 ? COLORS.present : (stats.compliance >= 0.7 ? COLORS.late : COLORS.absent)} />
            <Kpi label="Breaches" value={stats.totalBreachEvents} sub={`${stats.daysWithBreaches} day(s)`} accent={stats.totalBreachEvents ? COLORS.breach : undefined} />
          </section>

          {/* ---- Attendance breakdown pie + Hours line ---- */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChartCard title="Attendance breakdown" subtitle="Days in range by status">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={stats.presenceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={(e) => `${e.name}: ${e.value}`}
                  >
                    {stats.presenceData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} stroke="#0c1524" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0c1524", border: "1px solid #334155" }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Hours per day" subtitle={`Threshold ${stats.minHours} h`}>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={stats.dailyBars}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ background: "#0c1524", border: "1px solid #334155" }} />
                  <Line type="monotone" dataKey="hours" stroke={COLORS.ok} strokeWidth={2} dot={{ r: 3, fill: COLORS.ok }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>

          {/* ---- Breach frequency bar chart ---- */}
          <section>
            <ChartCard title="Breach frequency" subtitle="Presence-check heartbeats outside geofence per day">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.breachBars}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0c1524", border: "1px solid #334155" }} />
                  <Bar dataKey="breaches" fill={COLORS.breach} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>

          {/* ---- Averages + extremes ---- */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Avg / working day" value={stats.dailyAvg.toFixed(1) + " h"} sub={`Over ${stats.workingDaysInRange} day(s)`} />
            <Kpi label="Avg / week" value={stats.weeklyAvg.toFixed(1) + " h"} />
            <Kpi label="Longest day" value={stats.longest ? stats.longest.hours.toFixed(1) + " h" : "—"} sub={stats.longest ? format(parseISO(stats.longest.date), "d MMM") : ""} />
            <Kpi label="Shortest day" value={stats.shortest ? stats.shortest.hours.toFixed(1) + " h" : "—"} sub={stats.shortest ? format(parseISO(stats.shortest.date), "d MMM") : ""} />
          </section>

          {/* ---- Day-by-day table ---- */}
          <section>
            <Card className="rounded-none border-border print:border-black">
              <div className="p-3 border-b border-border print:border-black label-mono">Day-by-day log</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-3 py-2 label-mono">Date</th>
                      <th className="text-left px-3 py-2 label-mono">Status</th>
                      <th className="text-left px-3 py-2 label-mono">In ({tzLabel})</th>
                      <th className="text-left px-3 py-2 label-mono">Out ({tzLabel})</th>
                      <th className="text-right px-3 py-2 label-mono">Hours</th>
                      <th className="text-right px-3 py-2 label-mono">Breach</th>
                      <th className="text-left px-3 py-2 label-mono">Tags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.days.map((d) => {
                      const cell = empRow.days[d];
                      return (
                        <tr key={d} className="border-b border-border/60">
                          <td className="px-3 py-1.5 font-mono-brand">{format(parseISO(d), "d MMM · EEE")}</td>
                          <td className="px-3 py-1.5">{cell?.status || "—"}</td>
                          <td className="px-3 py-1.5 font-mono-brand">{formatTime(cell?.punch_in, activeTz)}</td>
                          <td className="px-3 py-1.5 font-mono-brand">{formatTime(cell?.punch_out, activeTz)}</td>
                          <td className="px-3 py-1.5 font-mono-brand text-right">{cell?.hours ? cell.hours.toFixed(1) : ""}</td>
                          <td className="px-3 py-1.5 font-mono-brand text-right">{cell?.breaches || ""}</td>
                          <td className="px-3 py-1.5 text-[10px] text-muted-foreground print:text-black">
                            {cell?.tags?.length ? cell.tags.join(" · ") : ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>

          <footer className="pt-4 border-t border-border text-[10px] text-muted-foreground text-center label-mono print:border-black print:text-black">
            {BRAND.fullName} · Signed, timestamped, tamper-evident attendance
          </footer>
        </article>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <Card className="rounded-none border-border p-3 print:border-black">
      <div className="label-mono">{label}</div>
      <div className="font-display font-bold text-2xl tabular-nums leading-tight" style={accent ? { color: accent } : {}}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground font-mono-brand mt-0.5 print:text-black">{sub}</div>}
    </Card>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <Card className="rounded-none border-border p-3 print:border-black">
      <div className="mb-1">
        <div className="label-mono">{title}</div>
        {subtitle && <div className="text-[10px] text-muted-foreground font-mono-brand print:text-black">{subtitle}</div>}
      </div>
      {children}
    </Card>
  );
}
